import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { passwordFieldLabels } from '../lib/form-labels.js';
import { useI18n } from '../lib/use-i18n.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { LocaleLink as Link } from '../lib/locale-link.js';
import { callTenantBootstrap } from '../lib/callables/bootstrap.js';
import { callGetAgentByCode } from '../lib/callables/agents.js';
import {
  dashboardLoginUrl,
  redirectToDashboardWithToken,
} from '../features/signup/redirect-to-dashboard.js';
import { logger } from '../lib/logger.js';
import { isKilatError, parseRateLimitError, phone as phoneUtil } from '@kilat/shared';
import { BoltIcon, PasswordField, PhoneField, PremiumField, ShieldIcon, Spinner } from '@kilat/ui';
import { LocaleSwitcher } from '../components/locale-switcher.js';

/**
 * /signup — operator instant trial activation per ADR-0015.
 *
 * Sprint 17 adds the agent-referral scan-to-signup loop:
 *   - `?ref=XXX-XXXX` in the URL prefills the agent code field, validates
 *     it via `getAgentByCode` callable, and shows "Introduced by {name}".
 *   - When prefilled-and-validated, the code is read-only (operator can
 *     clear it via the "Not introduced by an agent" link).
 *   - Validation has explicit loading / valid / invalid / inactive states
 *     so a missed scan or a stale code never looks like a hung form.
 *   - If the operator types a code manually, we don't auto-validate (avoids
 *     a callable on every keystroke). They submit and tenantBootstrap
 *     validates server-side — same code path, same error class.
 *
 * The operator never has to type the code if they scanned a QR — that's the
 * whole point of the referral flow.
 */
export function SignupPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const trialDays = useTrialDays();
  useMarketingSeo({
    title: t('marketing.signup.metaTitle', { brand: brand.name }),
    description: t('marketing.signup.metaDescription', { brand: brand.name, days: trialDays }),
  });
  const [params, setParams] = useSearchParams();

  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Live-normalised phone preview. The form accepts any user-typed
   * format ("+60 12-345 6789", "0123456789", "123456789") and shows
   * the canonical E.164 form below the input so the operator can see
   * the system parsed their number correctly before they submit.
   *
   * `null` means "not yet a valid MY number" — we never show an angry
   * red error pre-submit, just a quiet hint that goes green once
   * normalised, so the user doesn't feel scolded mid-typing.
   */
  const normalisedPhone = useMemo<string | null>(() => {
    if (!phone.trim()) return null;
    return phoneUtil.normaliseMalaysianPhone(phone);
  }, [phone]);
  const phoneHint =
    phone.trim().length === 0
      ? t('marketing.signup.phoneHintIdle')
      : normalisedPhone
        ? t('marketing.signup.phoneHintOk', {
            phone: phoneUtil.formatE164(normalisedPhone),
          })
        : t('marketing.signup.phoneHintBad');
  const phoneHintTone: 'muted' | 'success' | 'warn' =
    phone.trim().length === 0 ? 'muted' : normalisedPhone ? 'success' : 'warn';

  // Referral state (driven by the ?ref= query param).
  const refFromUrl = (params.get('ref') ?? '').trim().toUpperCase();
  const [refLookup, setRefLookup] = useState<
    | { stage: 'idle' }
    | { stage: 'loading'; code: string }
    | { stage: 'valid'; code: string; agentName: string }
    | { stage: 'invalid'; code: string; reason: string }
  >({ stage: 'idle' });

  useEffect(() => {
    if (!refFromUrl) {
      setRefLookup({ stage: 'idle' });
      return;
    }
    setRefLookup({ stage: 'loading', code: refFromUrl });
    setAgentCode(refFromUrl);
    let cancelled = false;
    (async () => {
      try {
        const result = await callGetAgentByCode({
          code: refFromUrl,
          originHost: window.location.hostname,
        });
        if (cancelled) return;
        setRefLookup({ stage: 'valid', code: result.code, agentName: result.name });
      } catch (err) {
        if (cancelled) return;
        const message = (err as Error).message ?? '';
        const reason = message.toLowerCase().includes('not currently active')
          ? "That referral code isn't active right now. You can sign up without it."
          : "We couldn't find that referral code. You can sign up without it.";
        logger.warn('ref lookup failed', { ref: refFromUrl, error: String(err) });
        setRefLookup({ stage: 'invalid', code: refFromUrl, reason });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refFromUrl]);

  function clearReferral() {
    const next = new URLSearchParams(params);
    next.delete('ref');
    setParams(next, { replace: true });
    setRefLookup({ stage: 'idle' });
    setAgentCode('');
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!normalisedPhone) {
      setError('Please enter a valid Malaysia mobile (e.g. +60123456789 or 0123456789).');
      return;
    }
    setSubmitting(true);
    try {
      const result = await callTenantBootstrap({
        businessName: businessName.trim(),
        ownerName: ownerName.trim(),
        email: email.trim(),
        phone: normalisedPhone,
        password,
        agentCode: agentCode.trim() || undefined,
        originHost: window.location.hostname,
        locale: brand.defaultLocale,
      });
      logger.info('signup ok', {
        tenantId: result.tenantId,
        slug: result.slug,
        referred: refLookup.stage === 'valid',
      });
      redirectToDashboardWithToken(result.signInToken);
    } catch (err) {
      // Rate-limited (Sprint 61: tenantBootstrap is capped at 3 per 10min
      // per IP). Surface the server's voice copy + how long until they
      // can retry, instead of a generic "Signup failed". Sprint 63.
      const rl = parseRateLimitError(err);
      if (rl) {
        logger.warn('signup rate-limited', { retryAfterSec: rl.retryAfterSec });
        const mins = Math.max(1, Math.round(rl.retryAfterSec / 60));
        setError(
          `${rl.message} ` +
            t('marketing.rateLimit.retrySuffix', {
              duration:
                mins === 1
                  ? t('marketing.rateLimit.minutesOne')
                  : t('marketing.rateLimit.minutesOther', { mins }),
            }),
        );
        setSubmitting(false);
        return;
      }
      const msg = isKilatError(err)
        ? err.code
        : ((err as Error).message ?? t('marketing.error.signupFailed'));
      setError(msg);
      logger.error('signup failed', { error: msg });
      setSubmitting(false);
    }
  }

  const refValid = refLookup.stage === 'valid';
  const [agentCodeOpen, setAgentCodeOpen] = useState(agentCode.length > 0);

  return (
    <main
      style={{
        minHeight: '100dvh',
        position: 'relative',
        background: '#fafaf7',
        color: '#0f172a',
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      {/* Layered ambient background — two soft radial gradients give the page
          depth without an image asset. Brand-primary tinted, low-opacity. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(80% 60% at 90% -10%, rgba(13,122,79,0.18), transparent 60%),' +
            'radial-gradient(60% 50% at -10% 10%, rgba(245,158,11,0.12), transparent 60%),' +
            'radial-gradient(70% 50% at 50% 110%, rgba(13,122,79,0.10), transparent 60%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* Faint grid texture — adds premium feel without being noisy. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(var(--tint-subtle) 1px, transparent 1px),' +
            'linear-gradient(90deg, var(--tint-subtle) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(70% 70% at 50% 30%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(70% 70% at 50% 30%, #000 30%, transparent 80%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1080,
          margin: '0 auto',
          padding: '20px 20px 64px',
        }}
      >
        {/* Top bar */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 4,
            paddingBottom: 16,
          }}
        >
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px 8px 8px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--color-border)',
              color: '#0f172a',
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 1px 2px var(--tint-subtle)',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                background: 'linear-gradient(135deg, #0d7a4f, #0f8659)',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              ←
            </span>
            <span>{brand.shortName}</span>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LocaleSwitcher />
            <a
              href="/pricing"
              style={{
                color: '#475569',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {t('marketing.cta.seePricing')} →
            </a>
          </div>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 28,
            marginTop: 24,
          }}
        >
          {/* Hero pitch column — sits above the form on mobile, beside it on desktop */}
          <section style={{ display: 'grid', gap: 16 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 999,
                background: 'rgba(13,122,79,0.10)',
                color: '#0d7a4f',
                fontSize: 11,
                letterSpacing: 1.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                width: 'fit-content',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: '#0d7a4f',
                }}
              />
              {t('marketing.signup.eyebrow')}
            </span>
            <h1
              style={{
                margin: 0,
                fontSize: 38,
                lineHeight: 1.05,
                fontWeight: 700,
                letterSpacing: -0.5,
                color: '#0f172a',
              }}
            >
              {t('marketing.signup.headlineLead')}{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('marketing.signup.headlineGradient')}
              </span>
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 15,
                lineHeight: 1.55,
                color: '#475569',
                maxWidth: 420,
              }}
            >
              {t('marketing.signup.subLead')} <strong>{t('marketing.signup.subEmphasis')}</strong>{' '}
              {t('marketing.signup.subTail')}
            </p>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginTop: 4,
              }}
            >
              {[
                t('marketing.signup.trustTrial', { days: trialDays }),
                t('marketing.signup.trustNoCard'),
                t('marketing.signup.trustPwa'),
                t('marketing.signup.trustWhatsapp'),
              ].map((label) => (
                <span
                  key={label}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid var(--color-border)',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#334155',
                  }}
                >
                  <span aria-hidden>{'✓'}</span> {label}
                </span>
              ))}
            </div>
          </section>

          {/* Form column — premium card */}
          <section>
            <div
              className="k-light-surface"
              style={{
                backdropFilter: 'blur(16px)',
                borderRadius: 24,
                border: '1px solid var(--color-border)',
                padding: '28px 24px',
                boxShadow:
                  '0 1px 2px var(--tint-subtle),' +
                  '0 8px 24px var(--color-border),' +
                  '0 24px 60px rgba(13,122,79,0.06)',
              }}
            >
              <ReferralBanner state={refLookup} onClear={clearReferral} />

              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'grid',
                  gap: 18,
                  marginTop: refValid || refLookup.stage !== 'idle' ? 20 : 0,
                }}
              >
                <PremiumField
                  label={t('marketing.signup.businessNameLabel')}
                  value={businessName}
                  onChange={setBusinessName}
                  autoComplete="organization"
                  placeholder={t('marketing.signup.businessNamePlaceholder')}
                  autoFocus
                  required
                  icon="building"
                />
                <PremiumField
                  label={t('marketing.form.nameLabel')}
                  value={ownerName}
                  onChange={setOwnerName}
                  autoComplete="name"
                  placeholder={t('marketing.form.namePlaceholder')}
                  required
                  icon="user"
                />
                <PremiumField
                  label={t('marketing.form.emailLabel')}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                  placeholder={t('marketing.signup.emailPlaceholder')}
                  required
                  icon="mail"
                />
                <PhoneField
                  label={t('marketing.form.phoneLabel')}
                  value={phone}
                  onChange={setPhone}
                  hint={phoneHint}
                  hintTone={phoneHintTone}
                />
                <PasswordField
                  label={t('marketing.form.passwordLabel')}
                  placeholder={t('marketing.form.passwordPlaceholder')}
                  labels={passwordFieldLabels(t)}
                  value={password}
                  onChange={setPassword}
                />

                {!refValid ? (
                  <div>
                    {agentCodeOpen ? (
                      <PremiumField
                        label={t('marketing.signup.agentCodeLabel')}
                        value={agentCode}
                        onChange={setAgentCode}
                        placeholder={t('marketing.signup.agentCodePlaceholder')}
                        autoCapitalize="characters"
                        icon="tag"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAgentCodeOpen(true)}
                        style={{
                          background: 'transparent',
                          color: '#475569',
                          border: 'none',
                          padding: '4px 0',
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            display: 'inline-grid',
                            placeItems: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: 999,
                            background: 'var(--color-border)',
                            fontSize: 12,
                            lineHeight: 1,
                          }}
                        >
                          +
                        </span>
                        {t('marketing.signup.agentCodePrompt')}
                      </button>
                    )}
                  </div>
                ) : null}

                {error ? (
                  <div
                    role="alert"
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      background: 'rgba(239,68,68,0.06)',
                      border: '1px solid rgba(239,68,68,0.22)',
                      color: '#991b1b',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    marginTop: 4,
                    position: 'relative',
                    background: submitting
                      ? '#0d7a4f'
                      : 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
                    color: '#ffffff',
                    padding: '16px 22px',
                    borderRadius: 14,
                    fontWeight: 600,
                    fontSize: 15,
                    border: 'none',
                    cursor: submitting ? 'wait' : 'pointer',
                    boxShadow: '0 1px 2px rgba(13,122,79,0.4),' + '0 8px 20px rgba(13,122,79,0.28)',
                    opacity: submitting ? 0.85 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                  }}
                  onMouseDown={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(1px)';
                  }}
                  onMouseUp={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                  }}
                >
                  {submitting ? (
                    <>
                      <Spinner /> {t('marketing.signup.submitting')}
                    </>
                  ) : (
                    <>
                      {t('marketing.signup.submit')}{' '}
                      <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>
                        →
                      </span>
                    </>
                  )}
                </button>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginTop: 4,
                    fontSize: 11,
                    color: '#64748b',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ShieldIcon /> {t('marketing.signup.trustEncrypted')}
                  </span>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <BoltIcon /> {t('marketing.signup.trustInstant')}
                  </span>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>{t('marketing.signup.trustSea')}</span>
                </div>

                <p
                  style={{
                    fontSize: 13,
                    color: '#475569',
                    textAlign: 'center',
                    margin: 0,
                  }}
                >
                  {t('marketing.signup.haveAccount')}{' '}
                  <a
                    href={dashboardLoginUrl()}
                    style={{ color: '#0d7a4f', textDecoration: 'none', fontWeight: 600 }}
                  >
                    {t('marketing.signup.signInLink')}
                  </a>
                </p>

                <p
                  style={{
                    fontSize: 12,
                    color: '#64748b',
                    textAlign: 'center',
                    margin: 0,
                    lineHeight: 1.55,
                  }}
                >
                  {t('marketing.signup.legalLead')}{' '}
                  <a
                    href={brand.links.tos}
                    style={{ color: '#0d7a4f', textDecoration: 'none', fontWeight: 500 }}
                  >
                    {t('marketing.signup.termsLink')}
                  </a>{' '}
                  &{' '}
                  <a
                    href={brand.links.privacy}
                    style={{ color: '#0d7a4f', textDecoration: 'none', fontWeight: 500 }}
                  >
                    {t('marketing.signup.privacyLink')}
                  </a>
                  .
                </p>
              </form>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

function ReferralBanner({
  state,
  onClear,
}: {
  readonly state:
    | { stage: 'idle' }
    | { stage: 'loading'; code: string }
    | { stage: 'valid'; code: string; agentName: string }
    | { stage: 'invalid'; code: string; reason: string };
  readonly onClear: () => void;
}) {
  const { t } = useI18n();
  if (state.stage === 'idle') return null;

  if (state.stage === 'loading') {
    return (
      <BannerShell tone="muted">
        <p style={{ margin: 0 }}>
          {t('marketing.signup.refLookingUp')} <strong>{state.code}</strong>…
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t('marketing.signup.refOneSecond')}
        </p>
      </BannerShell>
    );
  }

  if (state.stage === 'invalid') {
    return (
      <BannerShell tone="warn">
        <p style={{ margin: 0 }}>{state.reason}</p>
        <button
          type="button"
          onClick={onClear}
          style={{
            marginTop: 'var(--space-2)',
            background: 'transparent',
            color: 'var(--color-on-surface-muted)',
            border: '1px solid var(--color-border-strong)',
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
            cursor: 'pointer',
          }}
        >
          {t('marketing.signup.refOkWithout')}
        </button>
      </BannerShell>
    );
  }

  return (
    <BannerShell tone="accept">
      <p style={{ margin: 0 }}>
        {t('marketing.signup.refIntroBy')} <strong>{state.agentName}</strong> ({state.code})
      </p>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('marketing.signup.refCredit', { name: state.agentName })}
      </p>
      <button
        type="button"
        onClick={onClear}
        style={{
          marginTop: 'var(--space-2)',
          background: 'transparent',
          color: 'var(--color-on-surface-muted)',
          border: '1px solid var(--color-border-strong)',
          padding: '6px 10px',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-meta)',
          cursor: 'pointer',
        }}
      >
        {t('marketing.signup.refNotByAgent')}
      </button>
    </BannerShell>
  );
}

function BannerShell({
  tone,
  children,
}: {
  readonly tone: 'accept' | 'warn' | 'muted';
  readonly children: React.ReactNode;
}) {
  const style: React.CSSProperties = {
    marginTop: 'var(--space-6)',
    padding: 'var(--space-3) var(--space-4)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-body)',
  };
  if (tone === 'accept') {
    style.background = 'rgba(13,122,79,0.06)';
    style.border = '1px solid rgba(13,122,79,0.18)';
  } else if (tone === 'warn') {
    style.background = 'rgba(245,158,11,0.08)';
    style.border = '1px solid rgba(245,158,11,0.24)';
  } else {
    style.background = 'var(--tint-subtle)';
    style.border = '1px solid var(--color-border)';
  }
  return <aside style={style}>{children}</aside>;
}
